import React, {
  useState, useEffect, useRef, useCallback,
} from 'react';
import { startProofRender, fetchProofStatus } from '../shared/api';
import { InlineSpinner } from '../shared/Spinner';

// "Render true proof" (#4559).
//
// Every canvas in this designer — the editor, the thumbnails, Batch Test — is a
// separate implementation of the EC2 renderer, so none of them is authoritative.
// This panel asks the REAL renderer to render the season's live spec against a
// standing test student and shows what actually came back. It is the only
// output in the designer that is true by construction.
//
// The render lands in ~2 minutes, so a run is started and then polled.
const POLL_MS = 5000;

const STATUS_TEXT = {
  sent: 'Sent to the renderer — first poses usually land in about two minutes.',
  rendering: 'Rendering…',
  complete: 'Every pose the spec sells rendered.',
  partial: 'Stopped waiting. The poses below are everything that rendered.',
  error: 'Could not start.',
};

const ProofPanel = ({ season, lookTemplateId }) => {
  const [run, setRun] = useState(null);
  const [error, setError] = useState(null);
  const [starting, setStarting] = useState(false);
  const timerRef = useRef(null);

  const clearTimer = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  };

  // Unmount (or a season switch) must not leave a poll running against a run
  // the user can no longer see.
  useEffect(() => clearTimer, []);

  const poll = useCallback((runId) => {
    fetchProofStatus(runId)
      .then((data) => {
        setRun(data);
        if (!data.finished) timerRef.current = setTimeout(() => poll(runId), POLL_MS);
      })
      .catch(() => {
        // A transient poll failure shouldn't kill the run — keep waiting.
        timerRef.current = setTimeout(() => poll(runId), POLL_MS);
      });
  }, []);

  const start = () => {
    clearTimer();
    setError(null);
    setStarting(true);
    startProofRender(season, lookTemplateId)
      .then((data) => {
        setRun(data);
        if (!data.finished) timerRef.current = setTimeout(() => poll(data.id), POLL_MS);
      })
      .catch((err) => {
        setError(err?.response?.data?.error || 'Could not start a proof render.');
        setRun(null);
      })
      .finally(() => setStarting(false));
  };

  const inFlight = starting || (run && !run.finished);
  const results = run?.results || [];

  return (
    <div style={{ borderTop: '1px solid #e0e0e0', background: '#fafafa', flexShrink: 0 }}>
      <div style={{
        padding: '8px 16px', display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
      }}
      >
        <button
          type="button"
          onClick={start}
          disabled={inFlight || !season}
          title={season
            ? 'Render this season\'s published spec on the real renderer'
            : 'Pick a season first'}
          style={{
            padding: '6px 12px',
            fontSize: 12,
            fontWeight: 600,
            border: '1px solid #2e7d32',
            borderRadius: 4,
            background: inFlight ? '#eee' : '#e8f5e9',
            color: inFlight ? '#999' : '#1b5e20',
            cursor: inFlight || !season ? 'default' : 'pointer',
          }}
        >
          {inFlight ? (
            <>
              <InlineSpinner size={12} />
              {' '}
              Rendering…
            </>
          ) : 'Render true proof'}
        </button>

        {run && (
          <span style={{ fontSize: 11, color: '#555' }}>
            {STATUS_TEXT[run.status] || run.status}
            {run.expected_poses?.length > 0 && (
              <>
                {' '}
                <strong>
                  {run.produced_poses.length}
                  /
                  {run.expected_poses.length}
                </strong>
                {' poses'}
              </>
            )}
          </span>
        )}

        {run?.status === 'partial' && run.missing_poses?.length > 0 && (
          <span style={{ fontSize: 11, color: '#c62828' }}>
            {'never rendered: '}
            {run.missing_poses.join(', ')}
          </span>
        )}

        {error && <span style={{ fontSize: 11, color: '#c62828' }}>{error}</span>}
      </div>

      {results.length > 0 && (
        <div style={{
          display: 'flex', gap: 8, overflowX: 'auto', padding: '0 16px 12px',
        }}
        >
          {results.map((r) => {
            const isLookPose = run.look_pose != null && r.pose === run.look_pose;
            return (
              <a
                key={r.pose}
                href={r.url}
                target="_blank"
                rel="noopener noreferrer"
                title={`Pose ${r.pose} — open full size`}
                style={{
                  flexShrink: 0,
                  textDecoration: 'none',
                  textAlign: 'center',
                  border: `2px solid ${isLookPose ? '#1976d2' : '#ddd'}`,
                  borderRadius: 4,
                  padding: 3,
                  background: '#fff',
                }}
              >
                <img
                  src={r.url}
                  alt={`Pose ${r.pose}`}
                  style={{
                    display: 'block', height: 130, width: 'auto', background: '#f0f0f0',
                  }}
                />
                <span style={{
                  fontSize: 10,
                  color: isLookPose ? '#1976d2' : '#777',
                  fontWeight: isLookPose ? 700 : 400,
                }}
                >
                  {r.pose}
                  {isLookPose ? ' (this look)' : ''}
                </span>
              </a>
            );
          })}
        </div>
      )}

      {run && run.student_base && (
        <div style={{ padding: '0 16px 8px', fontSize: 10, color: '#999' }}>
          {`test student ${run.student_base} · rendered by EC2 block ${run.block_key} (spec ${run.spec_prefix}) · `
            + (run.draft_spec_key
              ? 'your SAVED look, published or not — save again and re-proof to see further edits'
              : 'the season\'s PUBLISHED spec')}
        </div>
      )}
    </div>
  );
};

export default ProofPanel;
